"use client";

import React, { useId, useState } from "react";
import { Eye, EyeOff, Lock, Pencil, X } from "lucide-react";
import { Button } from "@/components/atoms/Button";
import { Input } from "@/components/atoms/Input";
import { Label } from "@/components/atoms/Label";
import { TextArea } from "@/components/atoms/TextArea";
import type { BaseFieldProps, FieldConfig } from "./types";

/**
 * LOCKED_INPUT / LOCKED_TEXT_AREA — a field that cannot be edited until the admin
 * explicitly clicks "Edit". Read-only is the resting state; editing is opt-in.
 *
 * Why this exists (two real incidents, one control):
 *  1. Browser/password-manager autofill silently overwrote settings fields on page load.
 *     A locked field renders NO input element at all until Edit is clicked, so there is
 *     nothing on the page for autofill to land in. The unlocked input additionally opts
 *     out via a non-semantic random `name`, autoComplete="off"/"new-password", and the
 *     ignore attributes LastPass / 1Password / Bitwarden honour.
 *  2. Someone edits a field by accident, doesn't notice, and saves. Now every edit costs
 *     a deliberate click, and Cancel restores the value the field had when it was unlocked.
 *
 * SECRETS (`field.isSecret`): the plaintext never reaches the browser, so the locked view
 * renders one asterisk per stored character (`field.secretLength`, from the server's
 * `<field>_len`). That makes a stored secret visible AND removable:
 *   - never unlocked  → the parent omits the key entirely → server keeps the stored value
 *   - unlocked, blank → the key is sent blank → server REMOVES it
 *   - unlocked, typed → replaces it
 * Unlocking therefore starts EMPTY (we have no plaintext to prefill) and immediately
 * writes "" so a save right after unlocking reads as the removal it looks like.
 */

const MASK_CHAR = "*";
/** Fallback mask width when a secret is stored but its length can't be resolved. */
const UNKNOWN_LENGTH_MASK = 8;

type LockedFieldProps = BaseFieldProps & {
  field: FieldConfig;
  /** true → textarea (LOCKED_TEXT_AREA), false → single-line input (LOCKED_INPUT). */
  multiline?: boolean;
};

const LockedField: React.FC<LockedFieldProps> = ({
  fieldName,
  label,
  description,
  placeholder,
  disabled,
  className,
  required,
  hasError,
  validationMessage,
  formData,
  handleChange,
  field,
  multiline = false,
}) => {
  const [unlocked, setUnlocked] = useState(false);
  const [revealTyped, setRevealTyped] = useState(false);
  /** Value at unlock time, so Cancel is a true revert. */
  const [snapshot, setSnapshot] = useState<unknown>(undefined);

  // Non-semantic, per-mount field name: autofill heuristics key off name/id/label text,
  // so "gupshup_password" is a magnet and "f-«uid»" is not.
  const uid = useId();
  const domName = `f-${uid.replace(/[^a-zA-Z0-9]/g, "")}`;

  const isSecret = field.isSecret === true;
  const secretLength = Number(field.secretLength ?? 0);
  const secretIsSet = Boolean(field.secretIsSet) || secretLength > 0;

  const currentValue = formData[fieldName];
  const asText = currentValue == null ? "" : String(currentValue);

  /** What the locked box shows. Secrets never show a real value — only the mask. */
  const maskedSecret = secretIsSet
    ? MASK_CHAR.repeat(secretLength > 0 ? secretLength : UNKNOWN_LENGTH_MASK)
    : "";
  const lockedDisplay = isSecret ? maskedSecret : asText;

  const unlock = () => {
    setSnapshot(currentValue);
    // A secret starts blank (no plaintext exists client-side). Writing "" now is what
    // turns "unlocked and left blank" into an explicit removal on save.
    if (isSecret) handleChange(fieldName, "");
    setUnlocked(true);
  };

  const cancel = () => {
    handleChange(fieldName, snapshot);
    setRevealTyped(false);
    setUnlocked(false);
  };

  const fullWidth = Boolean(field.fullWidth) || multiline;
  const errorClasses = hasError
    ? "border-secondary-main focus:border-secondary-main focus:ring-secondary-main"
    : "";

  /** Attributes that opt the input out of browser + password-manager autofill. */
  const antiAutofill = {
    name: domName,
    id: domName,
    autoComplete: isSecret ? ("new-password" as const) : ("off" as const),
    autoCorrect: "off",
    autoCapitalize: "off",
    spellCheck: false,
    "data-lpignore": "true",
    "data-1p-ignore": "",
    "data-bwignore": "true",
    "data-form-type": "other",
  };

  return (
    <div
      className={`${fullWidth ? "w-full" : "flex-1 min-w-80 max-w-md"} mb-4`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {label && (
            <Label
              htmlFor={unlocked ? domName : undefined}
              className="block text-body-3-semibold font-semibold text-black"
            >
              {label}
              {field.showAsterisk !== false && required && (
                <span className="text-secondary-main ml-1">*</span>
              )}
            </Label>
          )}
          {description && (
            <p
              className={`text-body-4-medium text-gray-600 mt-1 ${fullWidth ? "" : "max-w-xl"}`}
            >
              {description}
            </p>
          )}
        </div>

        {!disabled && (
          <button
            type="button"
            onClick={unlocked ? cancel : unlock}
            className="shrink-0 inline-flex items-center gap-1.5 rounded-md border border-gray-3 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 transition-colors"
            title={
              unlocked
                ? "Discard changes to this field and lock it again"
                : "Unlock this field for editing"
            }
          >
            {unlocked ? (
              <>
                <X className="w-3.5 h-3.5" /> Cancel
              </>
            ) : (
              <>
                <Pencil className="w-3.5 h-3.5" /> Edit
              </>
            )}
          </button>
        )}
      </div>

      <div className="mt-2">
        {unlocked ? (
          <>
            <div className="relative">
              {multiline ? (
                <TextArea
                  {...antiAutofill}
                  value={asText}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    handleChange(fieldName, e.target.value)
                  }
                  placeholder={placeholder}
                  rows={field.rows}
                  autoFocus
                  classType="admin"
                  className={`w-full ${className || ""} ${errorClasses}`}
                />
              ) : (
                <Input
                  {...antiAutofill}
                  type={isSecret && !revealTyped ? "password" : "text"}
                  inputMode={field.valueType === "number" ? "numeric" : undefined}
                  value={asText}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleChange(fieldName, e.target.value)
                  }
                  placeholder={placeholder}
                  autoFocus
                  classType="admin"
                  className={`text-base md:text-sm placeholder:text-sm ${isSecret ? "pr-10" : ""} ${className || ""} ${errorClasses}`}
                />
              )}
              {isSecret && !multiline && (
                <button
                  type="button"
                  onClick={() => setRevealTyped((v) => !v)}
                  className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500 hover:text-gray-700"
                  title={revealTyped ? "Hide what you typed" : "Show what you typed"}
                >
                  {revealTyped ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              )}
            </div>
            <p className="text-body-4-medium text-gray-500 mt-1">
              {isSecret
                ? secretIsSet
                  ? "Type a new value to replace the stored one, or leave it blank and save to remove it."
                  : "Type a value and save to store it."
                : "Editing unlocked — save to apply, or Cancel to restore the previous value."}
            </p>
          </>
        ) : (
          <div
            className={`w-full p-3 border rounded-md bg-gray-50 text-sm break-words ${
              // Long bodies (llms.txt) scroll inside the box instead of stretching the page.
              multiline ? "max-h-64 overflow-y-auto" : ""
            } ${hasError ? "border-secondary-main" : "border-gray-3"}`}
            aria-readonly="true"
          >
            {lockedDisplay ? (
              <span
                className={`text-black ${multiline ? "whitespace-pre-wrap" : "truncate block"} ${
                  isSecret ? "tracking-[0.2em] font-mono" : ""
                }`}
              >
                {lockedDisplay}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-gray-500 italic">
                <Lock className="w-3.5 h-3.5" /> Not set
              </span>
            )}
          </div>
        )}
      </div>

      {hasError && (
        <p className="text-secondary-main text-body-4-medium mt-1">
          {validationMessage || "This field is required"}
        </p>
      )}
    </div>
  );
};

export default LockedField;
